import { useEffect, useState } from 'react'
import { httpsCallable } from 'firebase/functions'
import { Activity, Bot, CalendarDays, CheckCircle2, Clock3, Database, Download, Gauge, LoaderCircle, Play, RefreshCw, ShieldCheck, X } from 'lucide-react'
import { firebaseFunctions } from '../services/firebase'
import { useCommunity } from './CommunityProvider'
import { communityError } from './api'

type GenerationKind = 'state' | 'article' | 'all'
type AdminMessage = { tone: 'info' | 'success' | 'error'; text: string }
type JobHistory = {
  day: string
  status: string
  targetKind?: string
  sourceCount: number
  stateStatus?: string
  articleStatus?: string
  articleCount: number
  articleIds: string[]
  attemptCount: number
  reservedCostUsd: number
  durationMs: number
  startedAt?: string
  completedAt?: string
  error?: string
  stateId?: string
  articleId?: string
}
type Preflight = {
  sourceCount: number
  publisherCount: number
  fullTextSourceCount: number
  partialTextSourceCount: number
  summarySourceCount: number
  articleCandidateCount: number
  stateExists: boolean
  articleCount: number
  articleTarget: number
  running: boolean
  canGenerateState: boolean
  canGenerateArticle: boolean
  stateReason: string
  articleReason: string
}
type Status = JobHistory & {
  latestClosedDay: string
  earliestAvailableDay: string
  windowStart: string
  windowEnd: string
  dailyBudgetUsd: number
  remainingBudgetUsd: number
  articleTarget: number
  finalModel: string
  utilityModel: string
  retentionDays: number
  preflight: Preflight
  history: JobHistory[]
  refreshedAt: string
}
type BriefingEmailExport = { fileName: string; mimeType: string; count: number; exportedAt: string; csv: string }

const statusCall = httpsCallable<{ targetDate?: string }, Status>(firebaseFunctions, 'intelligenceAdminStatus')
const generateCall = httpsCallable<{ kind: GenerationKind; targetDate: string }, { skipped: boolean; complete?: boolean; reason?: string; day: string; stateId?: string; articleId?: string; articleIds?: string[] }>(firebaseFunctions, 'generateIntelligenceNow')
const exportBriefingCall = httpsCallable<void, BriefingEmailExport>(firebaseFunctions, 'exportBriefingEmailList')

const actionName = (kind: GenerationKind) => kind === 'state' ? 'State of AI' : kind === 'article' ? 'article batch' : 'daily pipeline'
const formatDateTime = (value?: string) => value ? new Date(value).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '—'
const formatDuration = (milliseconds: number) => milliseconds > 0 ? milliseconds < 1_000 ? `${milliseconds} ms` : `${(milliseconds / 1_000).toFixed(milliseconds < 10_000 ? 1 : 0)} sec` : '—'
const friendlyReason = (reason?: string) => reason === 'daily_limit' ? 'The selected edition is already complete.' : reason === 'leased' ? 'A generation job is already running for this edition.' : reason ?? 'The server skipped this run.'
const adminError = (error: unknown) => {
  const message = communityError(error)
  if (message.includes('future_edition_target')) return 'That edition window has not closed yet. Choose the latest closed edition.'
  if (message.includes('outside_retention')) return 'That edition is outside the retained source window.'
  if (message.includes('daily_budget_guard')) return 'The daily AI budget guard stopped this run before it could overspend.'
  if (message.includes('insufficient_')) return 'The evidence threshold was not met. Review the preflight source and publisher counts.'
  if (message.includes('unsupported_numeric_claim')) return 'Publication stopped because a numeric claim was not supported by the evidence.'
  return message
}

export function IntelligenceAdminPage() {
  const { user, openAccount } = useCommunity()
  const [status, setStatus] = useState<Status | null>(null)
  const [selectedDay, setSelectedDay] = useState('')
  const [message, setMessage] = useState<AdminMessage | null>(null)
  const [loading, setLoading] = useState(false)
  const [running, setRunning] = useState<GenerationKind | null>(null)
  const [pending, setPending] = useState<GenerationKind | null>(null)
  const [exporting, setExporting] = useState(false)
  const [lastExport, setLastExport] = useState<{ count: number; exportedAt: string } | null>(null)

  const applyStatus = (next: Status) => { setStatus(next); setSelectedDay(next.day) }
  const load = async (targetDate = selectedDay, clearMessage = true) => {
    setLoading(true)
    try {
      await user?.getIdToken(true)
      applyStatus((await statusCall(targetDate ? { targetDate } : {})).data)
      if (clearMessage) setMessage(null)
    } catch (error) { setMessage({ tone: 'error', text: adminError(error) }) } finally { setLoading(false) }
  }

  useEffect(() => {
    let cancelled = false
    if (!user) return () => { cancelled = true }
    Promise.resolve().then(() => {
      if (!cancelled) setLoading(true)
      return user.getIdToken(true)
    }).then(() => statusCall({})).then((response) => {
      if (!cancelled) { applyStatus(response.data); setMessage(null) }
    }).catch((error) => { if (!cancelled) setMessage({ tone: 'error', text: adminError(error) }) }).finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [user])

  const pollStatus = async (targetDate: string) => {
    try { applyStatus((await statusCall({ targetDate })).data) } catch { /* the foreground request reports the actionable error */ }
  }
  const generate = async (kind: GenerationKind) => {
    if (!status || !selectedDay) return
    setPending(null); setRunning(kind); setMessage({ tone: 'info', text: `Running the ${actionName(kind)} workflow for ${selectedDay}. This page will update while the server validates and publishes.` })
    const timer = window.setInterval(() => void pollStatus(selectedDay), 5_000)
    try {
      const result = (await generateCall({ kind, targetDate: selectedDay })).data
      setMessage(result.skipped
        ? { tone: 'info', text: `Skipped: ${friendlyReason(result.reason)}` }
        : result.complete === false
          ? { tone: 'info', text: `The ${actionName(kind)} workflow published every qualifying result it could for ${result.day}, but the daily target is not yet complete.` }
          : { tone: 'success', text: `The ${actionName(kind)} workflow completed successfully for ${result.day}.` })
    } catch (error) {
      setMessage({ tone: 'error', text: adminError(error) })
    } finally {
      window.clearInterval(timer); setRunning(null); await pollStatus(selectedDay)
    }
  }
  const downloadBriefingList = async () => {
    setExporting(true); setMessage({ tone: 'info', text: 'Preparing the active, consented briefing list.' })
    try {
      const result = (await exportBriefingCall()).data
      const url = URL.createObjectURL(new Blob([result.csv], { type: result.mimeType }))
      const link = document.createElement('a'); link.href = url; link.download = result.fileName; document.body.appendChild(link); link.click(); link.remove()
      window.setTimeout(() => URL.revokeObjectURL(url), 1_000)
      setLastExport({ count: result.count, exportedAt: result.exportedAt })
      setMessage({ tone: 'success', text: `Downloaded ${result.count} active briefing ${result.count === 1 ? 'address' : 'addresses'} as ${result.fileName}.` })
    } catch (error) { setMessage({ tone: 'error', text: adminError(error) }) } finally { setExporting(false) }
  }

  const canRunState = Boolean(status?.preflight.canGenerateState) && !running && !loading
  const canRunArticle = Boolean(status?.preflight.canGenerateArticle) && !running && !loading
  const canRunAll = (canRunState || canRunArticle) && !running && !loading
  const pendingLabel = pending ? actionName(pending) : ''

  return <article className="moderation-page intelligence-admin">
    <header><span className="eyebrow">RESTRICTED OPERATIONS</span><Bot/><h1>Intelligence operations</h1><p>Preflight evidence, run protected publication workflows, review recent outcomes, and export the private briefing list. No paid generation starts until you confirm it.</p></header>
    {!user ? <button className="moderation-signin" onClick={openAccount}>SIGN IN TO CONTINUE</button> : <>
      <section className="intelligence-admin-toolbar" aria-label="Edition controls">
        <label><CalendarDays/>EDITION DATE<input type="date" min={status?.earliestAvailableDay} max={status?.latestClosedDay} value={selectedDay} onChange={(event) => setSelectedDay(event.target.value)}/></label>
        <button disabled={loading || !selectedDay || selectedDay === status?.day} onClick={() => void load(selectedDay)}><RefreshCw className={loading ? 'is-spinning' : ''}/>{loading ? 'CHECKING…' : 'CHECK EDITION'}</button>
        <p>{status ? <>Evidence window <strong>{formatDateTime(status.windowStart)}</strong> through <strong>{formatDateTime(status.windowEnd)}</strong>.</> : 'Waking the protected operations service…'}</p>
      </section>

      <section className="intelligence-admin-status" aria-label="Selected edition status">
        <div><Gauge/><span>DAILY BUDGET CAP</span><strong>${status?.dailyBudgetUsd?.toFixed(2) ?? '—'}</strong><small>${status?.remainingBudgetUsd?.toFixed(2) ?? '—'} MAXIMUM REMAINING</small></div>
        <div><Activity/><span>EDITION · {(status?.day ?? selectedDay) || '—'}</span><strong>{loading && !status ? 'WAKING SERVER' : status?.status?.toUpperCase() ?? 'LOADING'}</strong><small>{status?.attemptCount ?? '—'} ATTEMPTS · {status?.retentionDays ?? '—'} DAY RETENTION</small></div>
        <div><Database/><span>EVIDENCE PREFLIGHT</span><strong>{status?.preflight.sourceCount ?? '—'} SOURCES</strong><small>{status?.preflight.publisherCount ?? '—'} PUBLISHERS · {status?.preflight.articleCandidateCount ?? '—'} ARTICLE CLUSTERS</small></div>
      </section>

      {status && <section className="intelligence-admin-preflight" aria-labelledby="preflight-heading">
        <header><div><span className="eyebrow">BEFORE YOU RUN</span><h2 id="preflight-heading">Edition preflight</h2></div><p>FINAL · {status.finalModel}<br/>UTILITY · {status.utilityModel}</p></header>
        <div className="intelligence-admin-health">
          <div className={status.preflight.sourceCount >= 3 ? 'is-ready' : 'is-blocked'}><span>USABLE CORPUS</span><strong>{status.preflight.sourceCount}</strong><small>{status.preflight.fullTextSourceCount} FULL · {status.preflight.partialTextSourceCount} PARTIAL · {status.preflight.summarySourceCount} SUMMARIES</small></div>
          <div className={status.preflight.canGenerateState ? 'is-ready' : 'is-complete'}><span>STATE OF AI</span><strong>{status.preflight.stateExists ? 'PUBLISHED' : status.preflight.canGenerateState ? 'READY' : 'BLOCKED'}</strong><small>{status.preflight.stateReason}</small></div>
          <div className={status.preflight.canGenerateArticle ? 'is-ready' : status.preflight.articleCount > 0 ? 'is-complete' : 'is-blocked'}><span>ORIGINAL ARTICLES</span><strong>{status.preflight.articleCount >= status.preflight.articleTarget ? 'PUBLISHED' : status.preflight.canGenerateArticle ? 'READY' : 'BLOCKED'}</strong><small>{status.preflight.articleCount} / {status.preflight.articleTarget} · {status.preflight.articleReason}</small></div>
          <div className={status.preflight.running ? 'is-running' : 'is-ready'}><span>JOB LOCK</span><strong>{status.preflight.running ? 'RUNNING' : 'AVAILABLE'}</strong><small>{status.preflight.running ? 'Duplicate runs are blocked by the active lease.' : 'No active generation lease.'}</small></div>
        </div>
      </section>}

      <nav className="intelligence-admin-actions" aria-label="Administrator actions">
        <button className="is-paid" disabled={!canRunState} title={status?.preflight.stateReason} onClick={() => setPending('state')}><Play/>GENERATE STATE OF AI</button>
        <button className="is-paid" disabled={!canRunArticle} title={status?.preflight.articleReason} onClick={() => setPending('article')}><Play/>GENERATE ARTICLES</button>
        <button className="is-paid" disabled={!canRunAll} title={!canRunAll ? 'No unpublished eligible output is ready.' : 'Run every eligible unpublished output.'} onClick={() => setPending('all')}><Play/>RUN ELIGIBLE PIPELINE</button>
        <button disabled={loading} onClick={() => void load(selectedDay)}><RefreshCw className={loading ? 'is-spinning' : ''}/>{loading ? 'REFRESHING…' : 'REFRESH STATUS'}</button>
        <button disabled={exporting} onClick={() => void downloadBriefingList()}><Download/>{exporting ? 'PREPARING EMAIL LIST…' : 'DOWNLOAD BRIEFING EMAIL LIST'}</button>
      </nav>

      {running && <div className="intelligence-admin-running" role="status"><LoaderCircle className="is-spinning"/><div><strong>RUNNING {actionName(running).toUpperCase()}</strong><span>Evidence and publication guards remain active. This can take several minutes.</span></div></div>}
      {message && <p className={`intelligence-admin-message is-${message.tone}`} role="status">{message.tone === 'success' && <CheckCircle2/>}{message.tone === 'error' && <X/>}{message.tone === 'info' && <Clock3/>}{message.text}</p>}
      {lastExport && <p className="intelligence-admin-export-note">LAST EMAIL EXPORT · {lastExport.count} ACTIVE ADDRESSES · {formatDateTime(lastExport.exportedAt)}</p>}

      {pending && status && <section className="intelligence-admin-confirm" role="alertdialog" aria-modal="true" aria-labelledby="confirm-generation-heading">
        <div><span className="eyebrow">PAID, PROTECTED ACTION</span><h2 id="confirm-generation-heading">Run {pendingLabel}?</h2><p>This targets edition <strong>{selectedDay}</strong>. It may use up to the remaining <strong>${status.remainingBudgetUsd.toFixed(2)}</strong> daily budget. Duplicate, evidence, citation, numeric-claim, and publication guards remain enforced.</p><nav><button onClick={() => setPending(null)}><X/>CANCEL</button><button className="is-confirm" onClick={() => void generate(pending)}><Play/>CONFIRM AND RUN</button></nav></div>
      </section>}

      <section className="intelligence-admin-history" aria-labelledby="run-history-heading">
        <header><div><span className="eyebrow">AUDIT TRAIL</span><h2 id="run-history-heading">Recent generation runs</h2></div><p>{status?.refreshedAt ? `REFRESHED ${formatDateTime(status.refreshedAt)}` : 'WAITING FOR STATUS'}</p></header>
        {status?.history.length ? <div>{status.history.map((job) => <article key={`${job.day}:${job.startedAt ?? 'unstarted'}`} className={`is-${job.status}`}><header><strong>{job.day}</strong><span>{job.status.toUpperCase()}</span></header><p>{job.sourceCount} SOURCES · {job.attemptCount} ATTEMPTS · ${job.reservedCostUsd.toFixed(3)} RESERVED · {formatDuration(job.durationMs)}</p><small>STATE · {job.stateStatus ?? 'NOT RUN'} &nbsp; ARTICLE · {job.articleStatus ?? 'NOT RUN'}</small>{job.error && <em>{job.error.replaceAll('_', ' ')}</em>}</article>)}</div> : <p className="intelligence-admin-empty">No generation runs are recorded yet.</p>}
      </section>

      <p className="intelligence-admin-note"><ShieldCheck/>The three generation controls are paid actions and require confirmation. Email export never sends mail. Server authentication and administrator claims enforce access to every operation.</p>
    </>}
  </article>
}
